import { ChangeDetectionStrategy, Component, computed, input, viewChildren, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { Layouts } from "./se.layouts";
import { FormField, FieldTree } from "@angular/forms/signals";
import { S25Util } from "../../../util/s25-util";
import { SeLayoutConfigWidgetComponent } from "./se.layout.config.widget.component";
import { S25InfoMessageModule } from "../../s25-info-message/s25.info.message.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25SimpleCollapseComponent } from "../../s25-simple-collapse/s25.simple.collapse.component";
import { CssRuleComponent } from "../../s25-styles/s25.css.rule.component";
import { LayoutsConfig } from "./se.layouts.config";
import { TelemetryService } from "../../../services/telemetry.service";

@Component({
    standalone: true,
    selector: "s25-ng-se-layout-config-section",
    template: `
        <div class="form-row">
            <label for="layout-config--section-heading">Heading</label>
            @if (readonly()) {
                <p>{{ section().heading().value() }}</p>
            } @else {
                <input id="layout-config--section-heading" type="text" [formField]="section().heading" />
                @for (error of section().heading().errors(); track $index) {
                    <p class="field-error">{{ error.message }}</p>
                }
            }
        </div>
        <div class="form-row">
            <label for="layout-config--section-heading">Heading Styles</label>
            <s25-ng-css-rule
                [styles]="section().headingCss()!.value()"
                (stylesChange)="setValue(section().headingCss!, $event)"
                [allowedProperties]="LayoutsConfig.allowedCssProperties"
                [readonly]="readonly()"
            />
        </div>
        <div class="form-row">
            <label [attr.for]="readonly() ? null : 'layout-config--section-columns'">Columns</label>
            @if (readonly()) {
                <p>{{ section().columns().value() ?? 1 }}</p>
            } @else {
                <select
                    id="layout-config--section-columns"
                    class="cn-form__control"
                    name="layout-config--section-columns"
                    [ngModel]="section().columns().value() ?? 1"
                    (ngModelChange)="setColumns($event)"
                >
                    <option [value]="1">1 column</option>
                    <option [value]="2">2 columns</option>
                </select>
            }
        </div>
        <div class="form-row visibility">
            <div class="visibility-label">
                <label for="layout-config--section-visibility">Visible To Roles</label>
                <s25-ng-info-message [isPopover]="true">
                    Select roles that can see this section. Selecting no roles will make the section visible to all
                    users.
                </s25-ng-info-message>
            </div>
            @if (readonly() && !visibleToRoles().length) {
                <p>All</p>
            } @else {
                <s25-ng-multiselect-search-criteria
                    [type]="'organizationRoles'"
                    [selectedItems]="visibleToRoles()"
                    [showResults]="true"
                    [readonly]="readonly()"
                    (onDone)="setVisibleToRoles($event)"
                />
                @for (error of section().visibleToRoles().errors(); track $index) {
                    <p class="field-error">{{ error.message }}</p>
                }
            }
        </div>
        <!-- Configure widgets -->
        <div class="divider-label">
            <h4 class="label">Widgets</h4>
            <s25-ng-button
                [type]="'outline'"
                (clicked)="expandOrCollapseAll()"
                [disabled]="!section().widgets().value().length"
            >
                {{ allCollapsed() ? "Expand" : "Collapse" }} All
            </s25-ng-button>
        </div>
        <div class="widgets">
            @for (widget of section().widgets; track widget; let w = $index) {
                @let widgetMeta = pageMeta().widgets[widget.type().value()]!.META;
                <ng-template #widgetHeader>
                    <div class="collapse-header">
                        <div class="collapse-header--label">
                            <span>{{ widgetMeta.name }}</span>
                            @if (widgetMeta.info) {
                                <s25-ng-info-message [isPopover]="true" (click)="$event.stopPropagation()">{{
                                    widgetMeta.info
                                }}</s25-ng-info-message>
                            }
                        </div>
                        @if (!readonly()) {
                            <div class="buttons icon-buttons">
                                <s25-ng-button
                                    [type]="'outline'"
                                    [disabled]="$index === 0"
                                    (clicked)="moveWidget(widget, -1)"
                                    [ariaLabel]="'Move up'"
                                >
                                    <s25-ng-icon [type]="'arrowUp'" [ariaLabel]="false" />
                                </s25-ng-button>
                                <s25-ng-button
                                    [type]="'outline'"
                                    [disabled]="$index === section().widgets.length - 1"
                                    (clicked)="moveWidget(widget, 1)"
                                    [ariaLabel]="'Move down'"
                                >
                                    <s25-ng-icon [type]="'arrowDown'" [ariaLabel]="false" />
                                </s25-ng-button>
                                <s25-ng-button
                                    [type]="'danger--outline'"
                                    (clicked)="removeWidget(widget)"
                                    [ariaLabel]="'Delete'"
                                >
                                    <s25-ng-icon [type]="'trash2'" [ariaLabel]="false" />
                                </s25-ng-button>
                            </div>
                        }
                    </div>
                </ng-template>
                <s25-simple-collapse
                    class="widget"
                    [header]="widgetHeader"
                    [buttonsSide]="'left'"
                    [class.invalid]="!widget().valid()"
                >
                    <s25-ng-se-layout-config-widget
                        class="collapse-content"
                        [widget]="widget"
                        [pageMeta]="pageMeta()"
                        [readonly]="readonly()"
                    />
                </s25-simple-collapse>
            }
            @if (!readonly()) {
                @for (error of section().widgets().errors(); track $index) {
                    <p class="field-error">{{ error.message }}</p>
                }
            }
        </div>
        @if (!readonly()) {
            <div class="buttons footer-buttons">
                <s25-ng-button [type]="'outline'" (clicked)="addWidget()"> + Add Widget</s25-ng-button>
            </div>
        }
    `,
    styles: `
        .widgets {
            display: grid;
            gap: 1rem;
        }

        .collapse-header--label {
            display: flex;
            gap: 0.5rem;
        }

        .form-row {
            padding-bottom: 1rem;
        }

        s25-ng-css-rule {
            width: fit-content;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        S25ButtonComponent,
        S25IconComponent,
        S25SimpleCollapseModule,
        FormsModule,
        FormField,
        SeLayoutConfigWidgetComponent,
        S25InfoMessageModule,
        S25MultiselectModule,
        CssRuleComponent,
    ],
})
export class SeLayoutConfigSectionComponent {
    readonly pageMeta = input.required<Layouts.PageConfig>();
    readonly section = input.required<FieldTree<Layouts.Section>>();
    readonly readonly = input(false);

    readonly collapses = viewChildren(S25SimpleCollapseComponent);
    readonly allCollapsed = computed(() => !!this.collapses()?.every((collapse) => !collapse.expanded()));

    protected readonly Layouts = Layouts;

    readonly widgetOptions = computed(() => {
        return S25Util.entries(this.pageMeta().widgets).map(([widget, component]) => ({
            id: widget,
            name: component.META.name,
            meta: component.META,
        }));
    });
    readonly visibleToRoles = computed(() => {
        return this.section()
            .visibleToRoles()
            .value()
            .map((role) => ({ itemId: role }));
    });

    moveWidget(item: FieldTree<Layouts.Widget>, dir: -1 | 1) {
        const widgets = this.section().widgets();
        const index = widgets.value().indexOf(item().value());
        const newIndex = index + dir;
        if (index === -1 || newIndex < 0 || newIndex >= widgets.value().length) return;
        widgets.value.update((items) => {
            const newItems = [...items];
            [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
            return newItems;
        });
        widgets.markAsDirty();
    }

    removeWidget(item: FieldTree<Layouts.Widget>) {
        const widgets = this.section().widgets();
        const index = widgets.value().indexOf(item().value());
        if (index === -1) return;
        widgets.value.update((items) => [...items.slice(0, index), ...items.slice(index + 1)]);
        widgets.markAsDirty();
    }

    addWidget() {
        const section = this.section();
        const widgetOption = this.widgetOptions()[0]!;
        const defaultSettings = Layouts.getDefaultSettingsForWidget(widgetOption.meta);
        section.widgets().value.update((widgets) => [
            ...widgets,
            {
                type: widgetOption.id,
                order: widgets.length,
                settings: defaultSettings,
                visibleToRoles: [],
            } satisfies Layouts.Widget,
        ]);
        section().markAsDirty();
        TelemetryService.sendWithSub("StudentEngagement", "Layouts", "WidgetAdded");
    }

    setColumns(value: number | string) {
        const columns = S25Util.parseInt(value) ?? 1;
        const columnsField = this.section().columns!;
        columnsField().value.set(columns);
        columnsField().markAsDirty();
    }

    setVisibleToRoles(roles: S25ItemI[]) {
        this.section()
            .visibleToRoles()
            .value.set(roles.map((role) => <number>role.itemId));
        this.section().visibleToRoles().markAsDirty();
    }

    expandOrCollapseAll() {
        const allCollapsed = this.allCollapsed();
        for (const collapse of this.collapses() ?? []) {
            if (allCollapsed) collapse.expand();
            else collapse.collapse();
        }
    }

    setValue(field: FieldTree<string>, value: string) {
        field().value.set(value);
        field().markAsDirty();
    }

    protected readonly LayoutsConfig = LayoutsConfig;
}
